'use client';

/**
 * DataTable — THE table for list surfaces.
 *
 * Headless state via @tanstack/react-table (typed columns, sorting, selection,
 * pagination models); rendering via ui/table primitives; states via
 * Skeleton / EmptyState / ErrorState so every list surface reads identically.
 *
 * Replaces the 14 raw <table> implementations found in the Phase 1 audit and
 * the dead ui/adaptive-table (whose cards-on-mobile idea lives on here via
 * `renderMobileCard`).
 *
 * Layout convention for list pages:
 *
 *   <PageShell>
 *     <PageHeader … />
 *     <TableToolbar actions={…}><SearchInput … />{filters}</TableToolbar>
 *     <DataTable columns={columns} data={rows} … />
 *     <TablePagination … />
 *   </PageShell>
 *
 * Server-driven lists (search/pagination on the API): pass `manualPagination`
 * (+ `pageCount`) and `manualSorting`, keep fetching in the page — the table
 * never owns data-fetching. Client lists: pass `clientPagination` /
 * let sorting default to the built-in sorted row model.
 */

import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox, MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { cn } from '@/lib/utils';

export type DataTableDensity = 'compact' | 'default' | 'comfortable';

/** Per-column presentation extras, set via the column's `meta`:
 *  responsive hiding (`hidden lg:table-cell`), alignment, widths. */
export interface DataTableColumnMeta {
  headClassName?: string;
  cellClassName?: string;
}

function columnMeta(def: { meta?: unknown }): DataTableColumnMeta {
  return (def.meta as DataTableColumnMeta | undefined) ?? {};
}

const DENSITY_CELL_CLASS: Record<DataTableDensity, string> = {
  compact: '[&_td]:py-2',
  default: '[&_td]:py-3',
  comfortable: '[&_td]:py-4',
};

/**
 * Row-click guard: clicks (and Enter/Space) originating on interactive
 * elements inside a row belong to those elements, not to onRowClick. Cells
 * can also opt a whole region out declaratively with `data-row-inert`
 * (e.g. the padding around a checkbox or an inline picker).
 */
const ROW_CLICK_GUARD_SELECTOR =
  'button, a, input, select, textarea, label, [role="checkbox"], [role="button"], [role="menuitem"], [role="menu"], [data-row-inert]';

function isRowInteractionGuarded(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(ROW_CLICK_GUARD_SELECTOR));
}

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /** Stable row id (defaults to array index — pass one for selection). */
  getRowId?: (row: TData, index: number) => string;

  /** Loading skeleton (row count matches `loadingRows`, no layout shift). */
  loading?: boolean;
  loadingRows?: number;
  /** Error state with optional retry — replaces the body. */
  error?: string | null;
  onRetry?: () => void;
  /** Empty state — a ready node, or omit for the default. */
  emptyState?: React.ReactNode;

  /** Row click (ignored when clicking interactive elements inside the row). */
  onRowClick?: (row: TData) => void;

  /** Bulk selection — controlled from the page (add `dataTableSelectColumn()`
   *  as the first column to get header/row checkboxes). */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;

  /** Sorting — omit for uncontrolled client sorting; set `manualSorting` when
   *  the server sorts. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;

  /** Pagination. `clientPagination` slices locally (uncontrolled);
   *  `manualPagination` + `pageCount` for server-driven lists. Render controls
   *  with <TablePagination>. */
  clientPagination?: { pageSize: number };
  manualPagination?: boolean;
  pageCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;

  density?: DataTableDensity;
  /** Sticky header — pairs with a max-height/overflow container via className. */
  stickyHeader?: boolean;
  /** Classes on the <table> element itself (e.g. `table-fixed` when explicit
   *  column widths must govern layout). */
  tableClassName?: string;
  /** Cards-on-mobile fallback. Selection/click still work via the same state. */
  renderMobileCard?: (row: TData) => React.ReactNode;
  /** Extra classes per row — for visually banding related rows together
   *  (e.g. one candidate's several applications) without changing row identity. */
  rowClassName?: (row: TData, index: number) => string | undefined;
  className?: string;
  /** Announced table label for screen readers. */
  'aria-label'?: string;
}

export function DataTable<TData>({
  columns,
  data,
  getRowId,
  loading = false,
  loadingRows = 5,
  error = null,
  onRetry,
  emptyState,
  onRowClick,
  rowSelection,
  onRowSelectionChange,
  sorting,
  onSortingChange,
  manualSorting = false,
  clientPagination,
  manualPagination = false,
  pageCount,
  pagination,
  onPaginationChange,
  density = 'default',
  stickyHeader = false,
  tableClassName,
  renderMobileCard,
  rowClassName,
  className,
  'aria-label': ariaLabel,
}: DataTableProps<TData>) {
  const { isMobile } = useBreakpoint();

  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalSelection, setInternalSelection] = React.useState<RowSelectionState>({});
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: clientPagination?.pageSize ?? 10,
  });

  const paginationEnabled = Boolean(clientPagination) || manualPagination;

  const table = useReactTable<TData>({
    data,
    columns,
    getRowId,
    state: {
      sorting: sorting ?? internalSorting,
      rowSelection: rowSelection ?? internalSelection,
      ...(paginationEnabled ? { pagination: pagination ?? internalPagination } : {}),
    },
    onSortingChange: onSortingChange ?? setInternalSorting,
    onRowSelectionChange: onRowSelectionChange ?? setInternalSelection,
    onPaginationChange: onPaginationChange ?? setInternalPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    ...(clientPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    manualSorting,
    manualPagination,
    ...(manualPagination && pageCount != null ? { pageCount } : {}),
    enableRowSelection: Boolean(rowSelection ?? onRowSelectionChange) || undefined,
  });

  const visibleColumns = table.getAllLeafColumns().length;

  // ---- states -------------------------------------------------------------

  if (error) {
    return (
      <div className={cn('rounded-lg border bg-card', className)}>
        <ErrorState size="sm" description={error} onRetry={onRetry} />
      </div>
    );
  }

  if (loading) {
    if (isMobile && renderMobileCard) {
      return (
        <div className={cn('space-y-3', className)} aria-busy="true">
          {Array.from({ length: Math.min(loadingRows, 4) }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      );
    }
    return (
      <div className={cn('rounded-lg border bg-card overflow-hidden', className)} aria-busy="true">
        <Table aria-label={ariaLabel}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {table.getFlatHeaders().map((header) => (
                <TableHead key={header.id} className={columnMeta(header.column.columnDef).headClassName}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className={DENSITY_CELL_CLASS[density]}>
            {Array.from({ length: loadingRows }, (_, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                {Array.from({ length: visibleColumns }, (_, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-full max-w-[12rem]" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn('rounded-lg border bg-card', className)}>
        {emptyState ?? <EmptyState icon={Inbox} title="No results" description="Nothing to show here yet." />}
      </div>
    );
  }

  // ---- mobile cards -------------------------------------------------------

  if (isMobile && renderMobileCard) {
    return (
      <div className={cn('space-y-3', className)} role="list" aria-label={ariaLabel}>
        {table.getRowModel().rows.map((row) => (
          <div key={row.id} role="listitem">
            {renderMobileCard(row.original)}
          </div>
        ))}
      </div>
    );
  }

  // ---- table --------------------------------------------------------------

  const handleRowClick = (event: React.MouseEvent, row: Row<TData>) => {
    if (!onRowClick) return;
    if (isRowInteractionGuarded(event.target)) return;
    onRowClick(row.original);
  };

  // Keyboard path for clickable rows (rows are focusable when onRowClick is
  // set). Only fires when the ROW itself has focus — Enter/Space on an inner
  // control (checkbox, link, picker) stays that control's event.
  const handleRowKeyDown = (event: React.KeyboardEvent, row: Row<TData>) => {
    if (!onRowClick) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    onRowClick(row.original);
  };

  return (
    <div className={cn('rounded-lg border bg-card overflow-hidden', className)}>
      <Table aria-label={ariaLabel} className={tableClassName}>
        <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10')}>
          <TableRow className="hover:bg-transparent">
            {table.getFlatHeaders().map((header) => {
              const sortDir = header.column.getIsSorted();
              return (
                <TableHead
                  key={header.id}
                  aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : undefined}
                  style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  className={columnMeta(header.column.columnDef).headClassName}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody className={DENSITY_CELL_CLASS[density]}>
          {table.getRowModel().rows.map((row, rowIndex) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() ? 'selected' : undefined}
              aria-selected={row.getIsSelected() || undefined}
              onClick={(e) => handleRowClick(e, row)}
              onKeyDown={(e) => handleRowKeyDown(e, row)}
              tabIndex={onRowClick ? 0 : undefined}
              className={cn(
                onRowClick &&
                  'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                rowClassName?.(row.original, rowIndex),
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={columnMeta(cell.column.columnDef).cellClassName}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column helpers
// ---------------------------------------------------------------------------

/** Header/row checkboxes for bulk selection — add as the FIRST column. */
export function dataTableSelectColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'select',
    size: 40,
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(Boolean(value))}
        aria-label="Select all rows"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(Boolean(value))}
        aria-label="Select row"
      />
    ),
  };
}

/** Sortable column header — click/Enter toggles asc → desc. */
export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: {
  column: Column<TData, TValue>;
  title: string;
  className?: string;
}) {
  if (!column.getCanSort()) {
    return <span className={className}>{title}</span>;
  }
  const sorted = column.getIsSorted();
  return (
    <button
      type="button"
      onClick={() => column.toggleSorting(sorted === 'asc')}
      className={cn(
        '-ml-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 uppercase tracking-wide',
        'hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
    >
      {title}
      {sorted === 'asc' ? (
        <ArrowUp className="h-3 w-3" aria-hidden="true" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="h-3 w-3" aria-hidden="true" />
      ) : (
        <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden="true" />
      )}
    </button>
  );
}

export interface DataTableRowAction {
  label: string;
  onSelect: () => void;
  /** Renders in destructive color (delete/archive). */
  destructive?: boolean;
  disabled?: boolean;
}

/** Standard "⋯" row-actions menu — keyboard accessible via Radix DropdownMenu. */
export function DataTableRowActions({
  actions,
  label = 'Open row actions',
}: {
  actions: DataTableRowAction[];
  label?: string;
}) {
  if (actions.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={label}>
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            disabled={action.disabled}
            onSelect={action.onSelect}
            className={cn(action.destructive && 'text-destructive focus:text-destructive')}
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type { ColumnDef, RowSelectionState, SortingState, PaginationState, TanstackTable };
